import { XMarkIcon } from '@heroicons/react/16/solid';
import { useEffect, useState } from 'react';
import { Input, Select } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { useModalsContext } from '../../../../../../providers/ModalsProvider/Modals.context.ts';
import { eAlertType } from '../../../../../../providers/ToastProvider/Toast.enum.ts';
import { useToastContext } from '../../../../../../providers/ToastProvider/Toast.hook.ts';
import { useUserContext } from '../../../../../../providers/UserProvider/User.context.ts';
import { useWorkspaceContext } from '../../../../../../providers/WorkspaceProvider/Workspace.context.ts';
import { removeEmptyValuesFromObject } from '../../../../../../utils/removeEmptyValuesFromObject.ts';
import { ChatEnum } from '../../../../Chat.enum.ts';
import { useChatContext } from '../../../../Chat.hook.ts';
import { ChatService } from '../../../../Chat.service.ts';
import {
  tAttachment,
  tNewQuickAction,
  tQuickReplyApiAttachment,
  tUpdateQuickAction,
} from '../../../../Chat.types.ts';
import { useMessageBarContext } from '../../MessageBar.hook.ts';
import LegacyInput from '../LegacyInput/LegacyInput.component.tsx';
import { tQuickActionProps } from '../QuickActionsAction/QuickAction.types.ts';
import './CreateQuickAction.styles.scss';
import { getModuleName } from '../../../../Chat.utils.ts';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';

export default function CreateQuickAction(props: tQuickActionProps) {
  const { trackEvent } = useAnalytics();
  const { currentWorkspace } = useWorkspaceContext();
  const {
    fetchQuickReplies,
    selectedChat,
    setRows,
    quickReplies,
    setQuickReplies,
  } = useChatContext();
  const {
    messageBarValue,
    setMessageBarValue,
    setSelectedAction,
    setChatAttachments,
    draftMessages,
    setDraftMessages,
  } = useMessageBarContext();
  const { selectedLeadForChatModal } = useModalsContext();
  const { t } = useTranslation('chat/chat');

  const [commandHandle, setCommandHandle] = useState<string>(
    props.quickActionState.title ?? '',
  );
  const [publicFilter, setPublicFilter] = useState<boolean>(true);
  const { currentUserId } = useUserContext();
  const { currentWorkspaceId } = useWorkspaceContext();
  const { addAlert } = useToastContext();
  const [loading, setLoading] = useState<boolean>(false);
  const [quickActionsAttachments, setQuickActionsAttachments] =
    useState<tAttachment[]>();

  const apiAttachments: tQuickReplyApiAttachment[] =
    quickActionsAttachments?.map((att) => ({
      mime_type: att.mimeType,
      name: att.filename,
      url: att.url,
      media_type: att.mediaType,
    })) ?? [];

  useEffect(() => {
    if (props.quickActionState) {
      setMessageBarValue(props.quickActionState.text);
      setQuickActionsAttachments(props.quickActionState.attachments);
      setPublicFilter(props.quickActionState.isPublic);
    }
  }, [props.quickActionState.id]);

  async function saveQuickAction() {
    if (
      !currentWorkspaceId ||
      !selectedChat ||
      !currentWorkspace ||
      !selectedLeadForChatModal
    )
      return;

    const payload: tNewQuickAction = {
      short_command_handle: commandHandle,
      message: messageBarValue.trim() || '',
      auth_id: currentUserId ?? '',
      workspace_id: currentWorkspaceId,
      is_public: publicFilter,
      attachment: apiAttachments,
    };

    try {
      setLoading(true);

      const nextDraftMessages = draftMessages ? [...draftMessages] : [];
      let currentDraft = -1;

      if (selectedChat?.leadId && Array.isArray(draftMessages)) {
        currentDraft = draftMessages.findIndex(
          (draft) => draft.leadId === selectedChat.leadId,
        );
      }
      await ChatService.createQuickReply({ data: payload });

      // The API response does not include the saved row (returns only
      // `{ result: { message } }`), so we cannot construct an optimistic
      // entry with a real id. Relying on the authoritative refetch avoids
      // inserting orphan rows with empty ids into local state. The refetch
      // is race-safe (see fetchQuickReplies stale-response guard).
      fetchQuickReplies();
      setSelectedAction(ChatEnum.QUICK_ACTIONS);
      setMessageBarValue('');
      setChatAttachments([]);
      if (currentDraft > -1) {
        nextDraftMessages.splice(currentDraft, 1);
        setDraftMessages(nextDraftMessages);
      }
      trackEvent('front.chat.quick_replies.create.save_btn.click', {
        displayName: 'Guardar respuesta rápida',
        description: 'Cuántas usuarias crean una respuesta rápida',
        ctaType: getModuleName(),
      });
      addAlert({
        type: eAlertType.SUCCESS,
        message: t('createQuickAction.savedCorrectly'),
      });
    } catch (error) {
      const err = error as { status?: number };

      if (err.status === 409) {
        addAlert({
          type: eAlertType.ERROR,
          message: `La respuesta rápida que intentas guardar ya existe. Intenta con un nombre diferente.`,
        });
      } else {
        addAlert({
          type: eAlertType.ERROR,
          message: `${t('createQuickAction.saveError')} ${err}`,
        });
      }
    }
    setRows(0);
    setLoading(false);
  }

  async function updateQuickAction() {
    try {
      if (!currentWorkspaceId || !selectedChat) return;

      const payload: tUpdateQuickAction = {
        short_command_handle: commandHandle,
        message: messageBarValue || '',
        auth_id: currentUserId ?? '',
        workspace_id: currentWorkspaceId,
        is_public: publicFilter,
        attachment: apiAttachments,
        id: props.quickActionState.id,
      };

      setLoading(true);
      await ChatService.updateQuickReply({ data: payload });

      const updatedQuickReplies =
        quickReplies?.map((quickAction) => {
          if (quickAction.id === props.quickActionState.id) {
            return {
              ...quickAction,
              content: {
                text: messageBarValue || '',
                attachments:
                  quickAction.content?.attachments?.map(
                    (attachment) =>
                      removeEmptyValuesFromObject(attachment) as tAttachment,
                  ) || [],
                linkPreview: null,
                quotedEventID: null,
              },
              shortCommand: commandHandle,
              isPublic: publicFilter,
            };
          }
          return quickAction;
        }) || null;

      setQuickReplies(updatedQuickReplies);
      fetchQuickReplies();

      addAlert({
        type: eAlertType.SUCCESS,
        message: t('createQuickAction.updateCorrectly'),
      });
      props.setQuickActionState({
        id: '',
        isPublic: true,
        title: '',
        text: '',
        attachments: [],
      });
      setMessageBarValue('');
      setSelectedAction(ChatEnum.QUICK_ACTIONS);
      setChatAttachments([]);
    } catch (error) {
      addAlert({
        type: eAlertType.ERROR,
        message: `${t('createQuickAction.updateError')} ${error}`,
      });
    }
    setRows(0);
    setLoading(false);
  }

  return (
    <div className="form-control absolute bottom-0 w-full z-10">
      <div className="CreateQuickAction">
        <div className="CreateQuickAction__Left">
          <strong>{t('createQuickAction.title')}</strong>
          <div className="CreateQuickAction__Left__InputContainer">
            <p>/</p>
            <Input
              onChange={(e) => setCommandHandle(e.target.value)}
              value={commandHandle}
              placeholder={t('createQuickAction.keyword')}
              className="CreateQuickAction__Left__InputContainer__Input"
              type="text"
              size="sm"
            />
          </div>
        </div>

        <div className="CreateQuickAction__Center">
          <strong>{t('createQuickAction.availableFor')}</strong>
          <Select
            size="sm"
            value={publicFilter ? '1' : '0'}
            onChange={(e) => setPublicFilter(e.target.value === '1')}
            className="CreateQuickAction__Center__Select"
          >
            <option value="1">
              {t('createQuickAction.availableOption01')}
            </option>
            <option value="0">
              {t('createQuickAction.availableOption00')}
            </option>
          </Select>
        </div>

        <XMarkIcon
          onClick={() => {
            props.setQuickActionState({
              id: '',
              isPublic: false,
              title: '',
              text: '',
              attachments: [],
            });
            setSelectedAction(ChatEnum.CHAT);
          }}
          className="CreateQuickAction__XIcon"
        />
      </div>
      <LegacyInput
        attachments={quickActionsAttachments}
        setNewAttachments={setQuickActionsAttachments}
        actionButtonText={
          props.quickActionState.id !== ''
            ? t('createQuickAction.updateButton')
            : t('createQuickAction.saveButton')
        }
        actionButtonLoading={loading}
        handleActionButton={
          props.quickActionState.id !== '' ? updateQuickAction : saveQuickAction
        }
      />
    </div>
  );
}
